import React, { useState, useEffect } from "react";
import {
  FaEdit,
  FaTimes,
  FaPlus,
  FaSpinner,
  FaTrash,
  FaCheck,
  FaExclamationTriangle,
  FaPhone,
} from "react-icons/fa";
import { SERVER_URL } from "../../constants.js";
import { toast } from "react-toastify";
import { useAuth, useUser } from "@clerk/clerk-react";

const AddressManager = () => {
  const { user } = useUser();
  const { getToken } = useAuth();

  const [addresses, setAddresses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const initialAddressState = {
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    notes: "",
  };

  useEffect(() => {
    const fetchAddresses = async () => {
      const token = await getToken();
      try {
        const response = await fetch(`${SERVER_URL}/api/v1/address/${user.id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.success) {
          setAddresses(result.addresses);
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
        toast.error("Failed to fetch addresses.");
      }
    };

    fetchAddresses();
  }, [user.id, getToken]);

  const validateForm = (address) => {
    const errors = {};
    if (!address.name.trim()) errors.name = "Name is required.";
    if (!address.email.trim()) errors.email = "Email is required.";
    if (!/\S+@\S+\.\S+/.test(address.email)) errors.email = "Invalid email format.";
    if (!address.address.trim()) errors.address = "Address is required.";
    if (!address.city.trim()) errors.city = "City is required.";
    if (!address.state.trim()) errors.state = "State is required.";
    if (!/^\d{6}$/.test(address.pincode)) errors.pincode = "Valid 6-digit pincode is required.";
    if (!/^\d{10}$/.test(address.phone)) errors.phone = "Valid 10-digit phone number is required.";
    if (!phoneVerified) errors.phone = "Phone number must be verified.";
    return errors;
  };

  const handleSendOtp = async () => {
    if (!currentAddress.phone || !/^\d{10}$/.test(currentAddress.phone)) {
      setFormErrors((prev) => ({
        ...prev,
        phone: "Valid 10-digit phone number is required to send OTP.",
      }));
      return;
    }

    setLoading(true);
    const token = await getToken();
    try {
      const response = await fetch(`${SERVER_URL}/api/v1/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: `+91${currentAddress.phone}` }),
      });
      const result = await response.json();
      if (result.success) {
        setOtpSent(true);
        setIsOtpModalOpen(true);
        toast.success("OTP sent successfully!");
      } else {
        toast.error(result.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    setLoading(true);
    const token = await getToken();
    try {
      const response = await fetch(`${SERVER_URL}/api/v1/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: `+91${currentAddress.phone}`, otp }),
      });
      const result = await response.json();
      if (result.success) {
        setPhoneVerified(true);
        setIsOtpModalOpen(false);
        setOtp("");
        toast.success("Phone number verified successfully!");
      } else {
        toast.error(result.message || "Invalid OTP.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Failed to verify OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateForm(currentAddress);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    const token = await getToken();
    try {
      let response;
      if (currentAddress._id) {
        response = await fetch(`${SERVER_URL}/api/v1/address/${currentAddress._id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(currentAddress),
        });
      } else {
        response = await fetch(`${SERVER_URL}/api/v1/address`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...currentAddress, userId: user.id }),
        });
      }

      const result = await response.json();
      if (result.success) {
        if (currentAddress._id) {
          setAddresses((prev) =>
            prev.map((addr) => (addr._id === currentAddress._id ? currentAddress : addr))
          );
        } else {
          setAddresses((prev) => [...prev, result.address]);
        }
        toast.success(result.message);
        setIsModalOpen(false);
        setFormErrors({});
        setPhoneVerified(false); // Reset for next addition
        setOtpSent(false);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Failed to save address.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const token = await getToken();
    try {
      const response = await fetch(`${SERVER_URL}/api/v1/address/${addressToDelete._id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (result.success) {
        setAddresses((prev) =>
          prev.filter((addr) => addr._id !== addressToDelete._id)
        );
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error("Failed to delete address.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;
    if (name === "pincode") {
      formattedValue = value.replace(/\D/g, "").slice(0, 6);
    } else if (name === "phone") {
      formattedValue = value.replace(/\D/g, "").slice(0, 10);
      // Reset verification only if phone number changes
      if (formattedValue !== currentAddress.phone) {
        setPhoneVerified(false);
        setOtpSent(false);
      }
    }

    setCurrentAddress((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (formErrors[name]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto bg-white shadow-sm rounded-lg">
      <div className="flex justify-between items-center p-6 border-b">
        <h1 className="text-2xl font-semibold text-gray-800">My Addresses</h1>
        <button
          onClick={() => {
            setCurrentAddress(initialAddressState);
            setFormErrors({});
            setPhoneVerified(false);
            setOtpSent(false);
            setIsModalOpen(true);
          }}
          className="px-6 py-2 bg-gradient-to-r from-[#009DC4] to-[#008DB4] text-white rounded-lg hover:from-[#008DB4] hover:to-[#007A9B] transition-colors duration-200 flex items-center gap-2 shadow-md"
        >
          <FaPlus size={14} /> Add New Address
        </button>
      </div>

      <div className="p-6">
        {addresses.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No addresses found. Add a new one!</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {addresses.map((address) => (
              <div
                key={address._id}
                className="bg-white border rounded-lg p-5 hover:shadow-md transition-shadow duration-300"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{address.name}</h3>
                <div className="space-y-2 text-gray-600">
                  <p>{address.address}</p>
                  <p>{address.city}, {address.state} - {address.pincode}</p>
                  <p className="flex items-center gap-2">
                    <span className="text-gray-500">📱</span> +91 {address.phone}
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-gray-500">✉️</span> {address.email}
                  </p>
                </div>
                <div className="flex gap-4 mt-4 pt-4 border-t">
                  <button
                    onClick={() => {
                      setCurrentAddress(address);
                      setFormErrors({});
                      setPhoneVerified(true); // Assume existing addresses are verified
                      setOtpSent(false);
                      setIsModalOpen(true);
                    }}
                    className="text-[#009DC4] hover:text-[#008DB4] flex items-center gap-1"
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    onClick={() => {
                      setAddressToDelete(address);
                      setIsDeleteModalOpen(true);
                    }}
                    className="text-red-500 hover:text-red-600 flex items-center gap-1"
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Address Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl">
            <div className="p-6 border-b bg-gradient-to-r from-[#009DC4] to-[#008DB4] text-white">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">
                  {currentAddress?._id ? "Edit Address" : "Add Shipping Address"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:text-gray-200"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSave} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name Input */}
                <div>
                  <label className="block text-gray-600 mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={currentAddress?.name || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200"
                    placeholder="Enter full name"
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                  )}
                </div>

                {/* Address Input */}
                <div>
                  <label className="block text-gray-600 mb-2">Full Address</label>
                  <input
                    type="text"
                    name="address"
                    value={currentAddress?.address || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200"
                    placeholder="Enter your address"
                  />
                  {formErrors.address && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>
                  )}
                </div>

                {/* Email Input */}
                <div>
                  <label className="block text-gray-600 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={currentAddress?.email || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200"
                    placeholder="Enter email address"
                  />
                  {formErrors.email && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                  )}
                </div>

                {/* Phone Input with OTP */}
                <div>
                  <label className="block text-gray-600 mb-2">Phone</label>
                  <div className="flex items-center gap-2">
                    <span className="px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-l text-gray-600">
                      +91
                    </span>
                    <input
                      type="tel"
                      name="phone"
                      value={currentAddress?.phone || ""}
                      onChange={handleInputChange}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-r focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200"
                      placeholder="Enter 10-digit phone number"
                    />
                    {!phoneVerified && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={loading || otpSent}
                        className="px-4 py-2 bg-gradient-to-r from-[#009DC4] to-[#008DB4] text-white rounded-lg hover:from-[#008DB4] hover:to-[#007A9B] transition-all duration-200 flex items-center gap-2 shadow-md"
                      >
                        {loading ? <FaSpinner className="animate-spin" /> : <FaPhone />}
                        {otpSent ? "OTP Sent" : "Send OTP"}
                      </button>
                    )}
                  </div>
                  {formErrors.phone && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                  )}
                  {phoneVerified && (
                    <p className="text-green-500 text-sm mt-1 flex items-center gap-1">
                      <FaCheck /> Verified
                    </p>
                  )}
                </div>

                {/* City Input */}
                <div>
                  <label className="block text-gray-600 mb-2">City</label>
                  <input
                    type="text"
                    name="city"
                    value={currentAddress?.city || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200"
                    placeholder="Enter city"
                  />
                  {formErrors.city && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>
                  )}
                </div>

                {/* State Input */}
                <div>
                  <label className="block text-gray-600 mb-2">State</label>
                  <input
                    type="text"
                    name="state"
                    value={currentAddress?.state || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200"
                    placeholder="Enter state"
                  />
                  {formErrors.state && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.state}</p>
                  )}
                </div>

                {/* Pincode Input */}
                <div>
                  <label className="block text-gray-600 mb-2">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={currentAddress?.pincode || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200"
                    placeholder="Enter 6-digit pincode"
                  />
                  {formErrors.pincode && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.pincode}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <button
                  type="submit"
                  disabled={loading || !phoneVerified}
                  className="px-8 py-2.5 bg-gradient-to-r from-[#009DC4] to-[#008DB4] text-white rounded-lg hover:from-[#008DB4] hover:to-[#007A9B] transition-all duration-200 flex items-center gap-2 shadow-md disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" /> Saving...
                    </>
                  ) : (
                    "Save Address"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {isOtpModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Verify Phone Number</h2>
              <button
                onClick={() => setIsOtpModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Enter the 6-digit OTP sent to +91 {currentAddress?.phone}
            </p>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#009DC4] transition-all duration-200 mb-4"
              placeholder="Enter OTP"
            />
            <div className="flex justify-end gap-4">
              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-[#009DC4] to-[#008DB4] text-white rounded-lg hover:from-[#008DB4] hover:to-[#007A9B] transition-all duration-200 flex items-center gap-2 shadow-md"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                Verify
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <FaExclamationTriangle className="text-red-500 text-xl" />
              <h2 className="text-xl font-bold text-gray-800">Confirm Delete</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this address? This action cannot be undone.
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-md disabled:from-red-400 disabled:to-red-500"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" /> Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash /> Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressManager;
